import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authApi, ordersApi, getToken, setToken, normalizeOrder } from "../api/client.js";
import { LAUNCH_PLAN_ID, MEMBERSHIP_DAYS } from "../data/products.js";
import { getDeviceId } from "../utils/deviceId.js";

const AuthContext = createContext(null);

// Cache the signed-in user so the first paint already shows their name in the
// header — otherwise `user` is null until authApi.me() resolves, and the name
// popping in shifts the header layout (a visible flicker on every load).
const USER_KEY = "rf_user";
const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeCachedUser = (u) => {
  try {
    if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
};

export function AuthProvider({ children }) {
  // Seed from the cached user (if a token exists) so there's no null → name flip.
  const [user, setUserState] = useState(() => (getToken() ? readCachedUser() : null));
  const [orders, setOrders] = useState([]);
  const [ready, setReady] = useState(false);

  // setUser that keeps the localStorage cache in sync with React state.
  const setUser = (u) => {
    writeCachedUser(u);
    setUserState(u);
  };

  // Validate an existing token on load and hydrate the user.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (getToken()) {
        try {
          const { user: u, token } = await authApi.me();
          if (token) setToken(token);
          if (alive && u) setUser(u);
        } catch {
          // Token invalid/expired — clear both token and the cached name.
          setToken(null);
          if (alive) setUser(null);
        }
      } else {
        // No token: ensure no stale cached user lingers.
        setUser(null);
      }
      if (alive) setReady(true);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const refreshOrders = async () => {
    try {
      const { orders: list } = await ordersApi.mine();
      setOrders((list || []).map(normalizeOrder));
    } catch {
      /* not logged in or unreachable */
    }
  };

  // Load orders whenever a user becomes available.
  // `void` on the async call keeps the effect's return value undefined.
  useEffect(() => {
    if (user) void refreshOrders();
    else setOrders([]);
  }, [user]);

  // Founding-member status derived from the user's own order history — a
  // non-cancelled order containing the ₹9 plan opens a 30-day free-delivery
  // window from that order's date. Online (Razorpay) orders only count once
  // actually paid; COD/UPI orders count from the moment they're placed (there
  // is no separate online-verification step for those methods).
  const membership = useMemo(() => {
    const counts = (o) =>
      o.status !== "cancelled" &&
      (o.paymentMethod !== "online" || o.paymentStatus === "paid") &&
      o.lines.some((l) => l.id === LAUNCH_PLAN_ID);
    const memberOrders = orders.filter(counts);
    // `claimed` stays true forever once any qualifying order exists — the ₹9
    // offer is one-per-household, so re-showing "join" prompts after the
    // 30-day free-delivery window lapses would just error out at checkout.
    if (!memberOrders.length) return { active: false, claimed: false, since: null, expiresAt: null };
    const latest = memberOrders.reduce((a, b) => (new Date(a.date) > new Date(b.date) ? a : b));
    const since = new Date(latest.date);
    const expiresAt = new Date(since.getTime() + MEMBERSHIP_DAYS * 86400000);
    return { active: Date.now() < expiresAt.getTime(), claimed: true, since, expiresAt };
  }, [orders]);

  const api = useMemo(
    () => ({
      user,
      orders,
      membership,
      ready,
      refreshOrders,

      // Step 1 of sign-up: email a 6-digit verification code.
      requestSignupOtp: async (email) => {
        try {
          const { ttlMinutes } = await authApi.requestOtp(email);
          return { ok: true, ttlMinutes };
        } catch (e) {
          return { ok: false, error: e.message };
        }
      },

      // Step 2 of sign-up: the OTP already proved email ownership, so this
      // creates the account straight away — no password to set.
      signup: async ({ name, email, phone, otp }) => {
        try {
          const { user: u, token } = await authApi.register({ name, email, phone, otp });
          setToken(token);
          setUser(u);
          return { ok: true };
        } catch (e) {
          return { ok: false, error: e.message };
        }
      },

      login: async ({ email, password }) => {
        try {
          // API accepts email or phone in the `login` field.
          const { user: u, token } = await authApi.login({ login: email, password });
          setToken(token);
          setUser(u);
          return { ok: true };
        } catch (e) {
          return { ok: false, error: e.message };
        }
      },

      // Passwordless login, step 1: email a 6-digit code to an existing account.
      requestLoginOtp: async (email) => {
        try {
          const { ttlMinutes } = await authApi.requestLoginOtp(email.trim().toLowerCase());
          return { ok: true, ttlMinutes };
        } catch (e) {
          return { ok: false, error: e.message };
        }
      },

      // Passwordless login, step 2: verify the code and start the session.
      loginWithOtp: async (email, otp) => {
        try {
          const { user: u, token } = await authApi.loginWithOtp(email.trim().toLowerCase(), otp);
          setToken(token);
          setUser(u);
          return { ok: true };
        } catch (e) {
          return { ok: false, error: e.message };
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch {
          /* ignore */
        }
        setToken(null);
        setUser(null);
      },

      placeOrder: async ({ lines, total, name, phone, address, addressId, paymentMethod = "cod", wantAlerts }) => {
        const payload = {
          items: lines.map((l) => ({
            productId: l.id,
            name: l.name,
            quantity: l.qty,
            unit: l.unit,
            lineTotal: l.lineTotal,
          })),
          totalAmount: total,
          paymentMethod,
          addressId: addressId || undefined,
          customerName: name,
          customerPhone: phone,
          notes: address ? `Deliver to: ${address}` : undefined,
          wantAlerts: Boolean(wantAlerts),
          deviceId: getDeviceId() || undefined,
        };
        try {
          const { order } = await ordersApi.place(payload);
          const normalized = normalizeOrder(order);
          setOrders((prev) => [normalized, ...prev]);
          return { ok: true, order: normalized };
        } catch (e) {
          return { ok: false, error: e.message };
        }
      },

      // Cancel one of the signed-in user's own orders. The API re-checks the
      // status gate and handles any refund + the cancellation emails.
      cancelOrder: async (orderId, reason) => {
        try {
          const { order } = await ordersApi.cancel(orderId, reason);
          const normalized = normalizeOrder(order);
          setOrders((prev) => prev.map((o) => (o.id === normalized.id ? normalized : o)));
          return { ok: true, order: normalized };
        } catch (e) {
          // Refresh anyway — a stale local status is the usual cause of a reject.
          void refreshOrders();
          return { ok: false, error: e.message };
        }
      },
    }),
    [user, orders, membership, ready]
  );

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
